export const FOCUS_ENTRY_ATTRIBUTE = 'data-focus-entry';
export const FOCUS_REGION_ATTRIBUTE = 'data-focus-region';
export const FOCUS_REGION_KIND_ATTRIBUTE = 'data-focus-region-kind';
export const BOARD_COLUMN_CONTENT_ATTRIBUTE = 'data-board-column-content';

/**
 * Matches a board column's content area: its card list and the actions next to it. Checks that treat
 * focus or clicks inside a listbox specially (arrow and letter key guards, outside-click dismissal)
 * treat this area the same way, so the actions next to the cards behave as the cards do.
 */
export const BOARD_COLUMN_CONTENT_SELECTOR = `[${BOARD_COLUMN_CONTENT_ATTRIBUTE}]`;

const ARROW_KEY_OWNER_SELECTOR = [
    `[${FOCUS_REGION_ATTRIBUTE}]`,
    '[role="menu"]',
    '[role="menubar"]',
    '[role="listbox"]',
    BOARD_COLUMN_CONTENT_SELECTOR,
    '[role="combobox"]',
    '[role="dialog"]',
    '[role="grid"]',
    '[role="tree"]',
    '[role="tablist"]',
    '[role="radiogroup"]',
    '[role="separator"]',
    '[role="slider"]',
    '[role="spinbutton"]',
].join(', ');

export const mayClaimArrowKeys = (active: Element | null): boolean => {
    if (active === null || active === document.body || !document.body.contains(active)) {
        return true;
    }

    if (!(active instanceof HTMLElement)) {
        return false;
    }

    if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement ||
        active.isContentEditable
    ) {
        return false;
    }

    return active.closest(ARROW_KEY_OWNER_SELECTOR) === null;
};

/**
 * While a tile region is open, loose-focus key claims belong to the tile, not
 * to widgets behind it: a claim is blocked unless the claiming widget lives
 * inside one of the open tiles.
 */
export const isClaimBlockedByTileRegion = (container: HTMLElement | null): boolean => {
    const tileRegions = document.querySelectorAll<HTMLElement>(`[${FOCUS_REGION_KIND_ATTRIBUTE}="tile"]`);

    if (tileRegions.length === 0) {
        return false;
    }

    return container === null || ![...tileRegions].some((tileRegion) => tileRegion.contains(container));
};

export const focusRegion = (region: HTMLElement, options?: FocusOptions): void => {
    const entry = region.hasAttribute(FOCUS_ENTRY_ATTRIBUTE)
        ? region
        : region.querySelector<HTMLElement>(`[${FOCUS_ENTRY_ATTRIBUTE}]`);

    entry?.focus(options);
};

export const findMainRegion = (): HTMLElement | null =>
    document.querySelector<HTMLElement>(`[${FOCUS_REGION_KIND_ATTRIBUTE}="main"]`);
